/**
 * @fileoverview Money component to render amounts using the org's reporting currency.
 * @module shared/components/ui/Money
 */

import { ReactElement } from 'react'
import { i18next } from '@/lib/i18n'
import { cn } from '@/shared/lib/utils'
import { useReportingCurrency } from '@/features/settings/context/OrgSettingsProvider'
import { formatMoney } from '@/shared/utils/money'

/**
 * Props for Money component.
 */
export interface MoneyProps {
  /** Amount in major units (e.g., dollars) */
  value: number
  /** Override currency code (ISO-4217). Defaults to org reporting currency. */
  currency?: string | undefined
  /** Optional locale override (BCP 47) */
  locale?: string | undefined
  /** Minimum fraction digits (default 2) */
  minFraction?: number | undefined
  /** Maximum fraction digits (default 2) */
  maxFraction?: number | undefined
  /** Optional className for styling */
  className?: string | undefined
  /** Whether to include locale-specific grouping separators (default true) */
  useGrouping?: boolean | undefined
}

/**
 * Render a formatted money string using the org's reporting currency by default.
 * Shows an ellipsis while the currency is loading.
 *
 * @component
 * @example
 * ```tsx
 * <Money value={1234.56} />
 * <Money value={1234.56} currency="EUR" />
 * ```
 */
export function Money({
  value,
  currency,
  locale,
  minFraction = 2,
  maxFraction = 2,
  className,
  useGrouping = true,
}: MoneyProps): ReactElement {
  const { currency: orgCurrency, isLoading } = useReportingCurrency()
  const code = currency ?? orgCurrency

  if (!code) {
    // Currency not resolved yet; show a lightweight placeholder
    return (
      <span className={cn('inline-block align-baseline', className)} aria-busy={isLoading}>
        …
      </span>
    )
  }

  const resolvedLocale = locale ?? i18next.language
  const label = formatMoney(
    value,
    code,
    resolvedLocale,
    minFraction,
    maxFraction,
    'narrowSymbol',
    useGrouping
  )

  return <span className={className}>{label}</span>
}
